/**
 * <blog-post-toc>
 * Auto-generates a table of contents from H2/H3 elements inside a target container,
 * adds anchor IDs, supports smooth-scroll jumping with a configurable offset, and
 * highlights the currently visible section via IntersectionObserver.
 *
 * Attributes:
 *   data-target          CSS selector for the source container (default ".blog-post-content")
 *   data-scroll-offset   Pixel offset from viewport top when scrolling to anchor (default 96)
 */
class BlogPostToc extends HTMLElement {
  connectedCallback() {
    this.targetSelector = this.dataset.target || '.blog-post-content';
    this.scrollOffset = parseInt(this.dataset.scrollOffset || '96', 10);
    this.listEl = this.querySelector('[data-toc-list]');
    this.emptyEl = this.querySelector('[data-toc-empty]');
    this.jumpEls = Array.from(this.querySelectorAll('[data-toc-jump]'));

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#init(), { once: true });
    } else {
      this.#init();
    }
  }

  #init() {
    const container = document.querySelector(this.targetSelector);

    if (!container) {
      this.#showEmpty();
      this.#hideUnusedJumps();
      return;
    }

    const headings = Array.from(container.querySelectorAll('h2, h3'));

    if (headings.length === 0) {
      this.#showEmpty();
    } else {
      this.#buildTOC(headings);
      this.#wireScrollSpy(headings);
    }

    this.#wireJumpLinks();
    this.#hideUnusedJumps();
    this.#wireSmoothScroll();
    this.#applyScrollMargin(headings);
    this.#wireMobileToggle();
  }

  /**
   * Toggle the TOC nav + jumps on mobile via the heading button.
   * Desktop CSS disables the click via pointer-events.
   */
  #wireMobileToggle() {
    const toggle = this.querySelector('[data-toc-toggle]');
    if (!toggle) return;

    toggle.addEventListener('click', () => {
      if (window.matchMedia('(min-width: 1024px)').matches) return;
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
    });
  }

  /**
   * Show jump links only if their anchor target actually exists on the page.
   */
  #hideUnusedJumps() {
    this.jumpEls.forEach((el) => {
      const targetId = el.dataset.tocJump;
      if (targetId && document.getElementById(targetId)) {
        el.classList.remove('hidden');
        el.classList.add('flex');
      }
    });
  }

  #showEmpty() {
    if (this.emptyEl) this.emptyEl.classList.remove('hidden');
  }

  /**
   * Build the ordered list of TOC items, assigning slugified IDs to headings as needed.
   * @param {Element[]} headings
   */
  #buildTOC(headings) {
    if (!this.listEl) return;

    const usedIds = new Set();
    const fragment = document.createDocumentFragment();

    headings.forEach((heading) => {
      if (!heading.id) {
        const baseId = this.#slugify(heading.textContent || 'section');
        let id = baseId;
        let counter = 2;
        while (usedIds.has(id) || document.getElementById(id)) {
          id = `${baseId}-${counter}`;
          counter += 1;
        }
        heading.id = id;
      }
      usedIds.add(heading.id);

      const li = document.createElement('li');
      li.className = `blog-post-toc__item blog-post-toc__item--${heading.tagName.toLowerCase()}`;

      const a = document.createElement('a');
      a.href = `#${heading.id}`;
      a.textContent = heading.textContent || '';
      a.dataset.tocLink = heading.id;

      li.appendChild(a);
      fragment.appendChild(li);
    });

    this.listEl.appendChild(fragment);
  }

  /**
   * Add scroll-margin-top to anchor targets so the sticky header doesn't cover them.
   * @param {Element[]} headings
   */
  #applyScrollMargin(headings) {
    const value = `${this.scrollOffset}px`;

    headings.forEach((heading) => {
      heading.style.scrollMarginTop = value;
    });

    ['article-faq', 'article-products', 'article-related', 'article-recent'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.style.scrollMarginTop = value;
    });
  }

  /**
   * Smooth scrolling for in-page anchor clicks originating inside the TOC.
   */
  #wireSmoothScroll() {
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;
      const id = link.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (window.history && typeof window.history.replaceState === 'function') {
        window.history.replaceState(null, '', `#${id}`);
      }
    });
  }

  /**
   * Mark the TOC entry corresponding to the section the user is currently in.
   * The active heading is the last heading whose top has crossed the offset line —
   * stays highlighted until the next heading crosses, so the entire section is
   * marked while reading.
   * @param {Element[]} headings
   */
  #wireScrollSpy(headings) {
    const linkById = new Map();
    headings.forEach((heading) => {
      const link = this.querySelector(`[data-toc-link="${heading.id}"]`);
      if (link) linkById.set(heading.id, link);
    });

    let activeId = null;

    const setActive = (id) => {
      if (id === activeId) return;
      if (activeId && linkById.has(activeId)) {
        linkById.get(activeId).classList.remove('is-active');
      }
      if (id && linkById.has(id)) {
        linkById.get(id).classList.add('is-active');
      }
      activeId = id;
    };

    const update = () => {
      const threshold = this.scrollOffset + 1;
      let current = headings[0] || null;

      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= threshold) {
          current = heading;
        } else {
          break;
        }
      }

      // Edge case: if we're at the very bottom of the page, force the last heading active.
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (atBottom) current = headings[headings.length - 1];

      setActive(current ? current.id : null);
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /**
   * Smooth scroll for the dedicated FAQ / Products jump links inside the sidebar.
   * (Already covered by #wireSmoothScroll, kept for clarity if extended later.)
   */
  #wireJumpLinks() {
    /* no-op: jump-link clicks bubble up and are handled by #wireSmoothScroll */
  }

  /**
   * Convert a heading title to a URL-safe slug.
   * @param {string} input
   * @returns {string}
   */
  #slugify(input) {
    return input
      .toString()
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'section';
  }
}

if (!customElements.get('blog-post-toc')) {
  customElements.define('blog-post-toc', BlogPostToc);
}
